// Flow.jsx — Enhanced with interactive animations and modified styling
import React, { useEffect, useRef, useState } from "react";

/* ====== One Step Card ====== */
const StepCard = ({ n, title, desc, index, icon = "⬜" }) => {
  const ref = useRef(null);
  const [isClicked, setIsClicked] = useState(false); // State for click interaction

  // cursor-follow glow + tilt
  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    const rx = ((y - r.height / 2) / r.height) * -6;
    const ry = ((x - r.width / 2) / r.width) * 6;
    el.style.setProperty("--mx", `${x}px`);
    el.style.setProperty("--my", `${y}px`);
    el.style.setProperty("--rx", `${rx}deg`);
    el.style.setProperty("--ry", `${ry}deg`);
  };
  const onLeave = () => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty("--rx", `0deg`);
    el.style.setProperty("--ry", `0deg`);
  };

  // Click handler with bounce effect
  const onClick = () => {
    setIsClicked(!isClicked);
    const el = ref.current;
    if (el) {
      el.style.setProperty("--pulse", isClicked ? "0" : "1");
      el.classList.add("bounce");
      setTimeout(() => {
        el.style.setProperty("--pulse", "0");
        el.classList.remove("bounce");
      }, 500); // Bounce and pulse duration
    }
  };

  // Focus handler for shake effect
  const onFocus = () => {
    const el = ref.current;
    if (el) el.classList.add("shake");
  };
  const onBlur = () => {
    const el = ref.current;
    if (el) el.classList.remove("shake");
  };

  return (
    <div
      ref={ref}
      className="vx-card reveal"
      style={{ "--i": index }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      onClick={onClick}
      onFocus={onFocus}
      onBlur={onBlur}
      tabIndex={0}
      role="button"
      aria-label={`${n}. ${title} - Click for details`}
    >
      <div className="vx-top">
        <div className="vx-icon" aria-hidden="true">
          {icon}
        </div>
        <h3 className="vx-title">{title}</h3>
      </div>

      <p className="vx-desc">{desc}</p>
      {isClicked && (
        <p className="vx-extra" style={{ marginTop: "10px", color: "#64748b" }}>
          Clicked! Explore more: {title} involves advanced automation and tracking for
          optimal results.
        </p>
      )}

      {/* shine sweep + cursor glow + pulse effect */}
      <span className="vx-shine" aria-hidden="true" />
      <span className="vx-glow" aria-hidden="true" />
      <span className="vx-pulse" aria-hidden="true" />
    </div>
  );
};

/* ====== Whole Flow Section ====== */
export default function Flow() {
  const rootRef = useRef(null);

  // staggered reveal when in view
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const cards = root.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (ents) => {
        ents.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add("in");
            io.unobserve(en.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    cards.forEach((c) => io.observe(c));
    return () => io.disconnect();
  }, []);

  return (
    <section className="vx-wrap" ref={rootRef}>
      <header className="vx-head">
        <h2 className="vx-h2">How WhatsApp Ads Work</h2>
        <p className="vx-tag">
          Simple path from <strong>impression</strong> to{" "}
          <strong>conversation</strong> to <strong>conversion</strong>.
        </p>
      </header>

      <div className="vx-grid">
        <StepCard
          index={0}
          n="1"
          title="See Ad"
          desc="People see your ad on Facebook/Instagram with a Send Message CTA styled in your brand colors."
          icon="👀"
        />
        <StepCard
          index={1}
          n="2"
          title="Open WhatsApp"
          desc="A tap opens WhatsApp instantly with your pre-filled greeting and tracking params."
          icon="📱"
        />
        <StepCard
          index={2}
          n="3"
          title="Automate Reply"
          desc="Chatbot welcomes, answers FAQs, qualifies leads, and routes to the right flow or agent."
          icon="🤖"
        />
        <StepCard
          index={3}
          n="4"
          title="Convert"
          desc="Share catalog, collect details, send payment link, or hand off to sales—then log the conversion."
          icon="💸"
        />
      </div>

      {/* ====== Scoped CSS (no external libs) ====== */}
      <style>{`
        /* ---------- THEME ---------- */
        :root {
          --brand: #0078D4;      /* Vertex Suite blue (used for effects only) */
          --ink: #0f172a;        /* Dark gray for text */
          --muted: #64748b;      /* Muted gray for secondary text */
          --border: #e3ebf5;
          --shadow: 0 4px 18px rgba(0, 120, 212, 0.12);
          --focus: #60a5fa;
        }
        html, body { background: #fff; }

        /* ---------- LAYOUT ---------- */
        .vx-wrap {
          max-width: 1280px;
          margin: 0 auto;
          padding: 48px 24px 64px;
          color: var(--ink);
          font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;
        }
        .vx-head { text-align: center; margin-bottom: 18px; }
        .vx-h2 {
          font-size: 42px;
          line-height: 1.15;
          margin: 0 0 8px;
          font-weight: 900;
          letter-spacing: 0.2px;
          color: var(--ink); /* Removed blue */
        }
        .vx-tag { color: var(--muted); margin: 0; }
        .vx-tag strong { color: var(--ink); /* Removed blue from strong */ }

        .vx-grid {
          display: grid;
          gap: 22px;
          grid-template-columns: repeat(4, 1fr);
          margin-top: 18px;
        }

        /* ---------- CARD ---------- */
        .vx-card {
          position: relative;
          border: 1px solid var(--border);
          border-radius: 18px;
          background: #fff;
          padding: 22px 24px;
          box-shadow: 0 1px 6px rgba(0, 0, 0, 0.04);
          transform: perspective(900px) rotateX(var(--rx, 0deg)) rotateY(var(--ry, 0deg)) scale(1);
          transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease, background 0.25s ease;
          overflow: hidden;
          outline: none;
          cursor: pointer;
        }
        .vx-card:hover {
          transform: perspective(900px) rotateX(var(--rx, 0deg)) rotateY(var(--ry, 0deg)) scale(1.02);
          box-shadow: var(--shadow);
          border-color: #d8e3f2;
        }
        .vx-card:focus-visible {
          border-color: var(--ink); /* Changed to dark gray */
          box-shadow: 0 0 0 3px var(--focus);
        }
        .vx-card.clicked {
          background: #f9f9f9; /* Light gray background on click */
          transition: background 0.3s ease;
        }
        .vx-card.bounce {
          animation: bounceEffect 0.5s ease-out;
        }
        .vx-card.shake {
          animation: shakeEffect 0.5s ease-out;
        }

        /* top row */
        .vx-top { display: flex; align-items: center; gap: 14px; margin-bottom: 6px; }
        .vx-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          display: grid;
          place-items: center;
          font-size: 28px;
          background: #eef4fd;
          color: var(--ink); /* Changed to dark gray */
          box-shadow: inset 0 0 0 1px #dbe7fb;
          transition: transform 0.25s ease, box-shadow 0.25s ease;
        }
        .vx-card:hover .vx-icon {
          transform: translateY(-3px);
          box-shadow: inset 0 0 0 2px var(--ink); /* Changed to dark gray */
        }

        .vx-title { margin: 0; font-size: 20px; font-weight: 800; letter-spacing: 0.2px; color: var(--ink); /* Removed blue */ }
        .vx-desc { margin: 8px 0 0; color: var(--muted); line-height: 1.6; }
        .vx-extra {
          margin: 10px 0 0;
          color: var(--muted);
          font-size: 0.9rem;
          opacity: 0;
          transform: translateY(10px);
          transition: opacity 0.3s ease, transform 0.3s ease;
        }
        .vx-card.clicked .vx-extra {
          opacity: 1;
          transform: translateY(0);
        }

        /* shine sweep */
        .vx-shine {
          position: absolute;
          inset: -1px;
          background: linear-gradient(120deg, transparent 35%, rgba(255, 255, 255, 0.75) 50%, transparent 65%);
          transform: translateX(-160%);
          transition: transform 0.75s ease;
          pointer-events: none;
        }
        .vx-card:hover .vx-shine { transform: translateX(160%); }

        /* cursor-follow glow */
        .vx-glow {
          position: absolute;
          inset: 0;
          pointer-events: none;
          background: radial-gradient(220px circle at var(--mx, 50%) var(--my, 50%), rgba(0, 120, 212, 0.16), transparent 60%);
          opacity: 0;
          filter: blur(18px);
          transition: opacity 0.25s ease;
        }
        .vx-card:hover .vx-glow { opacity: 1; }

        /* pulse effect */
        .vx-pulse {
          position: absolute;
          inset: 0;
          pointer-events: none;
          background: radial-gradient(100px circle at center, rgba(0, 120, 212, 0.1), transparent 70%);
          opacity: var(--pulse, 0);
          transition: opacity 0.5s ease;
          animation: pulse 0.5s ease-out;
        }
        @keyframes pulse {
          0% { opacity: 0.1; transform: scale(0); }
          50% { opacity: 0.3; transform: scale(1.2); }
          100% { opacity: 0; transform: scale(1.5); }
        }

        /* new bounce effect */
        @keyframes bounceEffect {
          0%, 100% { transform: perspective(900px) rotateX(var(--rx, 0deg)) rotateY(var(--ry, 0deg)) scale(1); }
          50% { transform: perspective(900px) rotateX(var(--rx, 0deg)) rotateY(var(--ry, 0deg)) scale(1.05) translateY(-5px); }
        }

        /* new shake effect */
        @keyframes shakeEffect {
          0%, 100% { transform: perspective(900px) rotateX(var(--rx, 0deg)) rotateY(var(--ry, 0deg)) scale(1) translateX(0); }
          25% { transform: perspective(900px) rotateX(var(--rx, 0deg)) rotateY(var(--ry, 0deg)) scale(1) translateX(-3px); }
          75% { transform: perspective(900px) rotateX(var(--rx, 0deg)) rotateY(var(--ry, 0deg)) scale(1) translateX(3px); }
        }

        /* staggered on-scroll reveal */
        .reveal { opacity: 0; transform: translateY(12px); }
        .reveal.in { animation: rise 0.55s cubic-bezier(0.2, 0.7, 0.2, 1) both; animation-delay: calc(var(--i) * 90ms); }
        @keyframes rise { to { opacity: 1; transform: translateY(0); } }

        /* responsive */
        @media (max-width: 1100px) {
          .vx-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 640px) {
          .vx-wrap { padding: 36px 16px 52px; }
          .vx-grid { grid-template-columns: 1fr; }
          .vx-h2 { font-size: 32px; }
        }

        /* motion safe */
        @media (prefers-reduced-motion: reduce) {
          .vx-card, .vx-icon, .vx-shine, .vx-glow, .vx-pulse { transition: none !important; }
          .reveal, .reveal.in { opacity: 1 !important; transform: none !important; animation: none !important; }
          .vx-card.clicked .vx-extra { opacity: 1 !important; transform: none !important; }
          .vx-card.bounce, .vx-card.shake { animation: none !important; }
        }
      `}</style>
    </section>
  );
}